import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { ThemeContext } from "../context/ThemeContext";
import getCurrentSkin from "../utils/getCurrentSkin";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSearch, FaDownload, FaTimes } from "react-icons/fa";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const API_BASE = "https://nissi-india.com/api/dashboard";

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const moneyToNumber = (v) => {
  if (v === null || v === undefined) return 0;
  const s = String(v).replace(/₹|Rs\./gi, "").replace(/,/g, "").trim();
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
};

const fmtINR = (amount) =>
  new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(toNum(amount));

const EstimationDashBoardReport = () => {
  const { isDark, skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ localStorage helper
  const getLS = (key) =>
    typeof window !== "undefined" ? localStorage.getItem(key) || "" : "";

  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branchType = getLS("branchType") || getLS("branchtype");
  const userId = getLS("userId") || getLS("loginuserid");

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const status = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get("status") === "1" ? "1" : "0";
  }, []);

  const pageTitle =
    status === "1"
      ? "Estimation Approved Report"
      : "Estimation Waiting Report";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/estimation/details`, {
        headers: commonHeaders,
        params: { status },
      });

      if (!res.data?.success) {
        toast.error("Failed to load report");
        setRows([]);
        return;
      }

      setRows(res.data.list || []);
    } catch (err) {
      console.error(err);
      toast.error("Server error");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const filteredRows = useMemo(() => {
    const q = searchTerm.toLowerCase().trim();
    if (!q) return rows;

    return rows.filter(
      (r) =>
        String(r.fld_Estimationcode || "").toLowerCase().includes(q) ||
        String(r.fld_Projectname || "").toLowerCase().includes(q)
    );
  }, [rows, searchTerm]);

  const totalAmount = useMemo(
    () => filteredRows.reduce((sum, r) => sum + moneyToNumber(r.fld_Amount), 0),
    [filteredRows]
  );

  const downloadPDF = () => {
    const doc = new jsPDF("p", "mm", "a4");

    doc.setFontSize(14);
    doc.text(pageTitle, 14, 16);

    autoTable(doc, {
      startY: 24,
      head: [["S.No", "Estimation Code", "Project Name", "Estimation Cost"]],
      body: filteredRows.map((r, i) => [
        i + 1,
        r.fld_Estimationcode,
        r.fld_Projectname,
        fmtINR(moneyToNumber(r.fld_Amount)),
      ]),
      styles: { fontSize: 9 },
      columnStyles: {
        0: { halign: "center", cellWidth: 12 },
        1: { halign: "center", cellWidth: 40 },
        2: { halign: "left", cellWidth: 90 },
        3: { halign: "right", cellWidth: 35 },
      },
    });

    doc.text(`TOTAL : ₹${fmtINR(totalAmount)}`, 14, doc.lastAutoTable.finalY + 8);

    doc.save(
      status === "1"
        ? "Estimation_Approved_Report.pdf"
        : "Estimation_Waiting_Report.pdf"
    );
  };

  return (
    <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
      <div
        className={`max-w-6xl mx-auto p-6 rounded-2xl shadow-xl border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 border-white/10 text-white"
            : "bg-white text-black"
        }`}
      >
        {/* Header */}
        <div className="flex flex-wrap justify-between items-center mb-6 gap-3">
          <div>
            <h1 className="text-2xl font-bold">{pageTitle}</h1>
            <p className="text-xs opacity-70">
              {loading ? "Loading..." : `Records : ${filteredRows.length}`}
            </p>
            <p className="text-xs opacity-60 mt-1">
              FinYear: {finYear || "-"} | Branch ID: {branchId || "-"} | Branch Type:{" "}
              {branchType || "-"} | User ID: {userId || "-"}
            </p>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..."
                className="pl-10 pr-4 py-2 rounded-lg border w-72"
              />
            </div>

            <button
              onClick={downloadPDF}
              className={`px-4 py-2 rounded-lg text-white flex items-center gap-2 ${buttonClass} ${hoverClass}`}
            >
              <FaDownload />
              PDF
            </button>

            <button
              onClick={() => window.close()}
              className="px-4 py-2 rounded-lg border flex items-center gap-2"
            >
              <FaTimes />
              Close
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto border rounded-xl">
          <table className="w-full">
            <thead className={`${buttonClass} text-white`}>
              <tr>
                <th className="px-4 py-3 border-r">S.No</th>
                <th className="px-4 py-3 border-r">Estimation Code</th>
                <th className="px-4 py-3 border-r">Project Name</th>
                <th className="px-4 py-3">Estimation Cost</th>
              </tr>
            </thead>

            <tbody>
              {filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center py-6">
                    {loading ? "Loading..." : "No Records Found"}
                  </td>
                </tr>
              ) : (
                filteredRows.map((r, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-4 py-3 text-center">{i + 1}</td>
                    <td className="px-4 py-3 text-center text-blue-600">
                      {r.fld_Estimationcode}
                    </td>
                    <td className="px-4 py-3">{r.fld_Projectname}</td>
                    <td className="px-4 py-3 text-right font-semibold">
                      ₹{fmtINR(moneyToNumber(r.fld_Amount))}
                    </td>
                  </tr>
                ))
              )}
            </tbody>

            <tfoot className={`${buttonClass} text-white`}>
              <tr>
                <td colSpan={3} className="px-4 py-3 text-center font-bold">
                  TOTAL
                </td>
                <td className="px-4 py-3 text-right font-bold">
                  ₹{fmtINR(totalAmount)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        <ToastContainer position="top-right" autoClose={3000} />
      </div>
    </div>
  );
};

export default EstimationDashBoardReport;